import React from "react";
import { router } from "@inertiajs/react";
import styled from "styled-components";
import { brand_orange_deep, brand_danger } from "../../constants/styles";
import { apiGet, apiPost } from "../../api";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";
import { useToast } from "../../commons/Toast";
import LessonReportModal from "./LessonReportModal";

// Diskusi panel for the rebranded lesson page. Renders:
//   - A composer at the top (signed-in only) that POSTs to
//     /comments/actions/create_ajax with { resource_type: "Lesson",
//     resource_id, reply_id, content }.
//   - A list of parent comments shipped via Inertia props
//     (`lesson.comments.latest`) plus any pages appended via the
//     paginated JSON endpoint at
//     /api/v1/sertifikasi/lessons/:id/comments?page=N.
//   - Inline reply form per parent (Balas), 👍 like + counter,
//     ⋯ Laporkan / Hapus overflow.
//
// Comment shape (server-authoritative — see
// app/serializers/sertifikasi/comment_serializer.rb):
//   { id, content, created_at, author: {id, name, avatar_url, is_self},
//     likes_count, is_liked_by_me, reply_count, replies: [<flat>] }

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const ms = new Date(iso).getTime();
    if (Number.isNaN(ms)) return "";
    const diff = Math.round((ms - Date.now()) / 1000);
    const abs = Math.abs(diff);
    const rtf = new Intl.RelativeTimeFormat("id", { numeric: "auto" });
    if (abs < 60) return rtf.format(diff, "second");
    if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
    if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), "day");
    if (abs < 86400 * 365) return rtf.format(Math.round(diff / (86400 * 30)), "month");
    return rtf.format(Math.round(diff / (86400 * 365)), "year");
  } catch (_) {
    return "";
  }
}

function Avatar({ name, avatarUrl, size = 36 }) {
  const initials = (name || "?").slice(0, 1).toUpperCase();
  if (avatarUrl) {
    return <AvatarImg src={avatarUrl} alt="" $size={size} />;
  }
  return (
    <AvatarFallback aria-hidden="true" $size={size}>
      {initials}
    </AvatarFallback>
  );
}

function Composer({ resourceId, replyId, onPosted, onCancel, autoFocus, placeholder = "Tulis komentar…" }) {
  const { signedIn } = React.use(LoggedInUserContext);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const ref = React.useRef(null);
  const { showToast } = useToast();

  React.useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const content = text.trim();
    if (!content) return;
    setBusy(true);
    setError(null);
    try {
      const { statusCode } = await apiPost(`/api/v1/sertifikasi/lessons/${resourceId}/comments`, {
        body: {
          comment: {
            reply_id: replyId || null,
            content,
          },
        },
      });
      if (statusCode >= 200 && statusCode < 300) {
        setText("");
        showToast({ tone: "success", message: replyId ? "Balasan dikirim" : "Komentar dikirim" });
        onPosted?.();
      } else {
        setError("Gagal mengirim. Silakan coba lagi.");
      }
    } catch (err) {
      console.warn("[Composer] post failed", err);
      setError("Gagal mengirim. Silakan coba lagi.");
    } finally {
      setBusy(false);
    }
  };

  if (!signedIn) {
    return (
      <SignInPanel>
        <span>Masuk untuk berdiskusi tentang materi ini.</span>
        <SignInLink href={`/login?redirect_after_login=${encodeURIComponent(window.location.pathname)}`}>
          Masuk →
        </SignInLink>
      </SignInPanel>
    );
  }

  return (
    <ComposerForm onSubmit={submit}>
      <ComposerTextarea
        ref={ref}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 500))}
        rows={replyId ? 2 : 3}
        aria-label={replyId ? "Tulis balasan" : "Tulis komentar"}
      />
      <ComposerFoot>
        <CharCount $warn={text.length >= 480}>{text.length}/500</CharCount>
        <ButtonRow>
          {onCancel && (
            <CancelButton type="button" onClick={onCancel} disabled={busy}>
              Batal
            </CancelButton>
          )}
          <SubmitButton type="submit" disabled={busy || !text.trim()}>
            {busy ? "Mengirim…" : replyId ? "Kirim Balasan" : "Kirim Komentar"}
          </SubmitButton>
        </ButtonRow>
      </ComposerFoot>
      {error && <InlineError role="alert">{error}</InlineError>}
    </ComposerForm>
  );
}

function CommentCard({ comment, lessonResourceId, onPosted, onReport, onDelete, onToggleLike, depth = 0 }) {
  const { signedIn } = React.use(LoggedInUserContext);
  const [replyOpen, setReplyOpen] = React.useState(false);
  const [showAllReplies, setShowAllReplies] = React.useState(false);
  const [overflowOpen, setOverflowOpen] = React.useState(false);
  const replies = comment.replies || [];
  const collapsed = !showAllReplies && replies.length >= 2;
  const visibleReplies = collapsed ? replies.slice(0, 1) : replies;

  return (
    <Card $depth={depth}>
      <Header>
        <Avatar name={comment.author?.name} avatarUrl={comment.author?.avatar_url} size={depth ? 28 : 36} />
        <Identity>
          <AuthorName>
            {comment.author?.name || "Pengguna"}
            {comment.author?.is_self && <SelfTag>Anda</SelfTag>}
          </AuthorName>
          <Stamp>{relativeTime(comment.created_at)}</Stamp>
        </Identity>
        {signedIn && (
          <OverflowWrap>
            <OverflowButton
              type="button"
              aria-label="Aksi komentar"
              onClick={() => setOverflowOpen((v) => !v)}
            >
              ⋯
            </OverflowButton>
            {overflowOpen && (
              <OverflowMenu role="menu" onMouseLeave={() => setOverflowOpen(false)}>
                {!comment.author?.is_self && (
                  <OverflowItem
                    role="menuitem"
                    onClick={() => {
                      setOverflowOpen(false);
                      onReport?.(comment);
                    }}
                  >
                    Laporkan
                  </OverflowItem>
                )}
                {comment.author?.is_self && (
                  <OverflowItem
                    role="menuitem"
                    $danger
                    onClick={() => {
                      setOverflowOpen(false);
                      onDelete?.(comment);
                    }}
                  >
                    Hapus
                  </OverflowItem>
                )}
              </OverflowMenu>
            )}
          </OverflowWrap>
        )}
      </Header>
      <Body>{comment.content}</Body>
      <Actions>
        <ActionButton
          type="button"
          aria-pressed={!!comment.is_liked_by_me}
          $active={!!comment.is_liked_by_me}
          onClick={() => onToggleLike?.(comment)}
        >
          <i className={`${comment.is_liked_by_me ? "fas" : "far"} fa-thumbs-up`} aria-hidden="true" />
          <span>{comment.likes_count || 0}</span>
        </ActionButton>
        {depth === 0 && signedIn && (
          <ActionButton type="button" onClick={() => setReplyOpen((v) => !v)}>
            💬 <span>Balas</span>
          </ActionButton>
        )}
        {depth === 0 && replies.length > 0 && (
          <ReplyCount>
            {replies.length} balasan
          </ReplyCount>
        )}
      </Actions>

      {depth === 0 && replyOpen && (
        <ReplyComposer>
          <Composer
            resourceId={lessonResourceId}
            replyId={comment.id}
            autoFocus
            placeholder="Tulis balasan…"
            onCancel={() => setReplyOpen(false)}
            onPosted={() => {
              setReplyOpen(false);
              onPosted?.();
            }}
          />
        </ReplyComposer>
      )}

      {visibleReplies.length > 0 && (
        <Replies>
          {visibleReplies.map((r) => (
            <CommentCard
              key={r.id}
              comment={r}
              lessonResourceId={lessonResourceId}
              onPosted={onPosted}
              onReport={onReport}
              onDelete={onDelete}
              onToggleLike={onToggleLike}
              depth={1}
            />
          ))}
          {collapsed && (
            <ShowMoreReplies type="button" onClick={() => setShowAllReplies(true)}>
              Lihat {replies.length - 1} balasan lainnya ▾
            </ShowMoreReplies>
          )}
        </Replies>
      )}
    </Card>
  );
}

function LessonComments({ lessonResourceId, comments }) {
  const initialList = comments?.latest || [];
  const initialNextUrl = comments?.next_page_url || null;
  const initialTotal = comments?.parent_count || 0;

  const [list, setList] = React.useState(initialList);
  const [nextUrl, setNextUrl] = React.useState(initialNextUrl);
  const [total, setTotal] = React.useState(initialTotal);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [reportingComment, setReportingComment] = React.useState(null);
  const { showToast } = useToast();

  React.useEffect(() => {
    setList(comments?.latest || []);
    setNextUrl(comments?.next_page_url || null);
    setTotal(comments?.parent_count || 0);
  }, [comments]);

  const handlePosted = () => {
    // Authoritative refresh of the parent-comments list on the page.
    router.reload({ only: ["lesson"] });
  };

  const handleLoadMore = async () => {
    if (!nextUrl || loadingMore) return;
    setLoadingMore(true);
    try {
      const { parsedResponse, statusCode } = await apiGet(nextUrl);
      if (statusCode >= 200 && statusCode < 300 && Array.isArray(parsedResponse?.data)) {
        setList((prev) => [...prev, ...parsedResponse.data]);
        setNextUrl(parsedResponse.next_page_url || null);
        if (parsedResponse?.meta?.total) setTotal(parsedResponse.meta.total);
      }
    } catch (err) {
      console.warn("[LessonComments] load more failed", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleToggleLike = async (comment) => {
    const prevLiked = !!comment.is_liked_by_me;
    const prevCount = Number(comment.likes_count) || 0;
    // Optimistic toggle
    setList((prev) => prev.map((c) => walkUpdate(c, comment.id, () => ({
      ...comment,
      is_liked_by_me: !prevLiked,
      likes_count: prevLiked ? Math.max(0, prevCount - 1) : prevCount + 1,
    }))));
    try {
      const { statusCode } = await apiPost("/user_likes/create_v2", {
        body: {
          user_likes: {
            resource_type: "Comment",
            resource_id: comment.id,
            likes: !prevLiked,
          },
        },
      });
      if (!(statusCode >= 200 && statusCode < 300)) {
        // rollback
        setList((prev) => prev.map((c) => walkUpdate(c, comment.id, () => comment)));
      }
    } catch (err) {
      console.warn("[LessonComments] like failed", err);
      setList((prev) => prev.map((c) => walkUpdate(c, comment.id, () => comment)));
    }
  };

  const handleDelete = async (comment) => {
    if (!window.confirm("Hapus komentar ini?")) return;
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || "";
      const res = await fetch(`/admin/comments/${comment.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrf,
        },
      });
      if (res.status >= 200 && res.status < 400) {
        showToast({ tone: "success", message: "Komentar dihapus" });
        handlePosted();
      } else {
        showToast({ tone: "danger", message: "Gagal menghapus komentar" });
      }
    } catch (err) {
      console.warn("[LessonComments] delete failed", err);
      showToast({ tone: "danger", message: "Gagal menghapus komentar" });
    }
  };

  return (
    <Section aria-labelledby="lesson-comments">
      <SectionHeader>
        <Title id="lesson-comments">Diskusi</Title>
        <Count>{total || comments?.count || 0} komentar</Count>
      </SectionHeader>

      <Composer
        resourceId={lessonResourceId}
        onPosted={handlePosted}
        placeholder="Bagikan pertanyaan atau pendapat tentang materi ini…"
      />

      {list.length === 0 ? (
        <Empty>Jadilah yang pertama berdiskusi tentang materi ini.</Empty>
      ) : (
        <Stack>
          {list.map((c) => (
            <CommentCard
              key={c.id}
              comment={c}
              lessonResourceId={lessonResourceId}
              onPosted={handlePosted}
              onReport={(comment) => setReportingComment(comment)}
              onDelete={handleDelete}
              onToggleLike={handleToggleLike}
            />
          ))}
        </Stack>
      )}

      {nextUrl && (
        <LoadMore type="button" onClick={handleLoadMore} disabled={loadingMore}>
          {loadingMore ? "Memuat…" : "Lihat 10 lainnya"}
        </LoadMore>
      )}

      {reportingComment && (
        <LessonReportModal
          commentId={reportingComment.id}
          onClose={() => setReportingComment(null)}
        />
      )}
    </Section>
  );
}

// Walk the parent + replies tree and apply `transform` to the matching id.
function walkUpdate(node, targetId, transform) {
  if (node.id === targetId) return transform(node);
  if (Array.isArray(node.replies) && node.replies.length > 0) {
    return { ...node, replies: node.replies.map((r) => walkUpdate(r, targetId, transform)) };
  }
  return node;
}

export default React.memo(LessonComments);

// ---- styles ----------------------------------------------------------------

const Section = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #1f2937;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const Count = styled.span`
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(216, 89, 7, 0.12);
  color: ${brand_orange_deep};
  font-weight: 700;
`;

const Empty = styled.div`
  padding: 16px;
  text-align: center;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 12px;
  font-size: 13px;
  [data-theme="dark"] & { background: rgba(255, 255, 255, 0.04); color: #9ca3af; }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  background: ${({ $depth }) => ($depth ? "transparent" : "#fff")};
  border: 1px solid ${({ $depth }) => ($depth ? "transparent" : "#f3f4f6")};
  border-radius: 12px;
  margin-left: ${({ $depth }) => ($depth ? "44px" : "0")};
  border-left: ${({ $depth }) => ($depth ? "2px solid #fed7aa" : "1px solid #f3f4f6")};

  [data-theme="dark"] & {
    background: ${({ $depth }) => ($depth ? "transparent" : "rgba(255,255,255,0.02)")};
    border-color: ${({ $depth }) => ($depth ? "transparent" : "rgba(255,255,255,0.06)")};
    border-left-color: ${({ $depth }) =>
      $depth ? "rgba(216, 89, 7, 0.40)" : "rgba(255,255,255,0.06)"};
  }
`;

const Header = styled.header`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: center;
`;

const AvatarImg = styled.img`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const AvatarFallback = styled.div`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fcd34d, #fb923c);
  color: #fff;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => Math.round($size * 0.42)}px;
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

const AuthorName = styled.span`
  font-weight: 700;
  font-size: 13px;
  color: #1f2937;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const SelfTag = styled.span`
  font-size: 10px;
  font-weight: 800;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(216, 89, 7, 0.14);
  color: ${brand_orange_deep};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Stamp = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

const OverflowWrap = styled.div`
  position: relative;
`;

const OverflowButton = styled.button`
  background: transparent;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 18px;
  font-weight: 800;
  color: #6b7280;
  cursor: pointer;
  &:hover { background: rgba(0,0,0,0.05); color: #1f2937; }
  &:focus-visible { outline: 2px solid ${brand_orange_deep}; outline-offset: 2px; }
`;

const OverflowMenu = styled.div`
  position: absolute;
  right: 0;
  top: 30px;
  min-width: 140px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.10);
  z-index: 10;
  overflow: hidden;
  [data-theme="dark"] & { background: #111827; border-color: rgba(255,255,255,0.08); }
`;

const OverflowItem = styled.button`
  display: block;
  width: 100%;
  padding: 9px 12px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ $danger }) => ($danger ? brand_danger : "#1f2937")};
  &:hover { background: ${({ $danger }) => ($danger ? "rgba(220,38,38,0.08)" : "#f9fafb")}; }
  [data-theme="dark"] & { color: ${({ $danger }) => ($danger ? "#fca5a5" : "#f3f4f6")};
    &:hover { background: rgba(255,255,255,0.04); } }
`;

const Body = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #374151;
  white-space: pre-wrap;
  word-wrap: break-word;
  [data-theme="dark"] & { color: #d1d5db; }
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 4px;
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  color: ${({ $active }) => ($active ? brand_orange_deep : "#6b7280")};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;

  & i { font-size: 14px; }

  &:hover { background: rgba(216, 89, 7, 0.08); color: ${brand_orange_deep}; }
  &:focus-visible { outline: 2px solid ${brand_orange_deep}; outline-offset: 2px; }
`;

const ReplyCount = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 600;
`;

const ReplyComposer = styled.div`
  margin-top: 8px;
  margin-left: 44px;
  border-left: 2px solid rgba(216, 89, 7, 0.18);
  padding-left: 12px;
`;

const Replies = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ShowMoreReplies = styled.button`
  background: transparent;
  border: none;
  margin-left: 44px;
  padding: 4px 0;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: ${brand_orange_deep};
  cursor: pointer;

  &:hover { text-decoration: underline; }
`;

const ComposerForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.10);
  }
`;

const ComposerTextarea = styled.textarea`
  width: 100%;
  resize: vertical;
  min-height: 60px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  background: #fff;

  &:focus-visible { outline: 2px solid ${brand_orange_deep}; outline-offset: 2px; }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(255, 255, 255, 0.08);
    color: #f3f4f6;
  }
`;

const ComposerFoot = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;

const CharCount = styled.span`
  font-size: 11px;
  color: ${({ $warn }) => ($warn ? brand_danger : "#9ca3af")};
  font-variant-numeric: tabular-nums;
`;

const ButtonRow = styled.div`
  display: flex; gap: 8px;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  border-radius: 999px;
  background: transparent;
  border: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: #f3f4f6; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SubmitButton = styled.button`
  padding: 8px 18px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  border: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover { filter: brightness(0.95); }
  &:disabled { opacity: 0.6; cursor: not-allowed; filter: none; }
`;

const InlineError = styled.div`
  font-size: 12px;
  color: ${brand_danger};
`;

const SignInPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 13px;
  color: #92400e;

  [data-theme="dark"] & {
    background: rgba(216, 89, 7, 0.08);
    border-color: rgba(216, 89, 7, 0.32);
    color: #fde68a;
  }
`;

const SignInLink = styled.a`
  background: ${brand_orange_deep};
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 8px 18px;
  border-radius: 999px;

  &:hover { filter: brightness(0.95); color: #fff; }
`;

const LoadMore = styled.button`
  align-self: center;
  margin-top: 8px;
  padding: 10px 22px;
  border-radius: 999px;
  background: transparent;
  border: 1px solid ${brand_orange_deep};
  color: ${brand_orange_deep};
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;

  &:hover { background: rgba(216, 89, 7, 0.08); }
  &:disabled { opacity: 0.6; cursor: wait; }
`;
